import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Dropdown, DropdownItem } from "flowbite-react";
import { Button } from "@/components/ui/Button/Button";
import { Typography } from "@/components/ui/Typography/Typography";
import { Badge } from "@/components/ui/Badge/badge";
import "./role-management-theme.css";
import { TAssignedPermission } from "@/api/role-management/role-management.types";
import {
  GET_ROLE_LIST_QUERY_KEY,
  usePermissionToggle,
  useBulkPermissionAssignment,
  useGetPermissionKPIQuery,
  usePermissionModules,
  useAssignedPermissions,
  useAvailablePermissions,
  useGetRoleByIdQuery,
} from "@/api/role-management/role-management.hooks";
import {
  ArrowLeft,
  Search,
  Shield,
  CheckCircle,
  Circle,
  Plus,
  Minus,
  Users,
  MoveLeft,
} from "lucide-react";
import { PermissionSelectionReviewModal } from "./PermissionSelectionReviewModal";
import { Input } from "@/components/forms/Input";
import { useTranslation } from "react-i18next";
import { useDebounce } from "@/hooks";
import KpiLoaders from "@/pages/layout/loaders/KpiLoaders";
import { PAGE_INDEX, PAGE_LIMIT_TEN } from "@/core/const";
import { PaginationState } from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import CommonPaginationCard from "@/components/ui/Table/CommonPaginationCard";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useBreadcrumb } from "@/hooks/useBreadcumb";
import CommonCardUI from "@/components/ui/Card/CommonCardUI";
import { cn } from "@/utils";
import { PermissionGuard } from "@/components/PermissionGuard";
import { usePermissions } from "@/hooks/usePermissions";
import ErrorFallback from "@/components/error-fallback/ErrorFallback";

// Use the enhanced permission types from the API
type TPermission = TAssignedPermission;

type TPermissionRowProps = {
  permission: TPermission;
  showCheckbox?: boolean;
  isChecked: boolean;
  isToggling: boolean;
  isBulkActionRunning: boolean;
  onSelect: (permission: TPermission) => void;
  onToggle: (permission: TPermission) => Promise<void>;
};

const PermissionRowComponent: React.FC<TPermissionRowProps> = ({
  permission,
  showCheckbox = true,
  isChecked,
  isToggling,
  isBulkActionRunning,
  onSelect,
  onToggle,
}: TPermissionRowProps) => {
  const { t } = useTranslation(["roleManagementPage", "common"]);
  return (
    <div className="group relative">
      <div
        className={`flex items-center gap-3 p-3 border-b ${isToggling || isBulkActionRunning ? "" : "hover:bg-muted/25  transition-colors"}`}
      >
        {showCheckbox && (
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => onSelect(permission)}
            className="rounded border-border"
          />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Typography variant="p" className="font-medium text-foreground">
              {permission.name ?? t("PERMISSION_ASSIGNMENT.UNNAMED_PERMISSION")}
            </Typography>
          </div>
        </div>

        <PermissionGuard permissions={["can-assign-permissions"]}>
          <Button
            variant={permission.is_assigned ? "destructive" : "default"}
            size="xs"
            onClick={() => void onToggle(permission)}
            disabled={isToggling || isBulkActionRunning}
            className={`flex items-center gap-1 transition-opacity duration-200
              ${
                isToggling
                  ? "opacity-100 pointer-events-auto"
                  : "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto"
              }
            `}
            id="permission-toggle-button"
          >
            {isToggling ? (
              <>
                <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                {permission.is_assigned
                  ? t("PERMISSION_ASSIGNMENT.REMOVING")
                  : t("PERMISSION_ASSIGNMENT.ASSIGNING")}
              </>
            ) : permission.is_assigned ? (
              <>
                <Minus className="w-3 h-3" />
                {t("PERMISSION_ASSIGNMENT.REMOVE")}
              </>
            ) : (
              <>
                <Plus className="w-3 h-3" />
                {t("PERMISSION_ASSIGNMENT.ASSIGN")}
              </>
            )}
          </Button>
        </PermissionGuard>
      </div>
    </div>
  );
};

const PermissionRow = React.memo(PermissionRowComponent);
PermissionRow.displayName = "PermissionRow";

type TPermissionSelectionPanelProps = {
  selectedIds: string[];
  isListLoading?: boolean;
  onReviewClick: () => void;
};

const PermissionSelectionPanel: React.FC<TPermissionSelectionPanelProps> = ({
  selectedIds,
  isListLoading = false,
  onReviewClick,
}) => {
  const { t } = useTranslation(["roleManagementPage"]);
  const count = selectedIds.length;

  if (count === 0 || isListLoading) return null;

  return (
    <div className="border-b border-border px-3 py-2 flex-shrink-0">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-indigo" />
          <Typography variant="small" className="font-medium text-indigo">
            {t("PERMISSION_ASSIGNMENT.SELECTED_COUNT", { count })}
          </Typography>
        </div>
        <Button variant="outline" size="xs" onClick={onReviewClick}>
          {t("PERMISSION_ASSIGNMENT.REVIEW")}
        </Button>
      </div>
    </div>
  );
};

export const PermissionAssignmentView: React.FC = () => {
  const queryClient = useQueryClient();
  const { roleId } = useParams({ strict: false });

  const navigate = useNavigate();

  const { t } = useTranslation(["roleManagementPage", "common"]);
  const { hasAnyPermission } = usePermissions();
  const [searchTerm, setSearchTerm] = useState("");

  const [assignedPagination, setAssignedPagination] = useState<PaginationState>(
    {
      pageIndex: PAGE_INDEX,
      pageSize: PAGE_LIMIT_TEN,
    },
  );

  const [availablePagination, setAvailablePagination] =
    useState<PaginationState>({
      pageIndex: PAGE_INDEX,
      pageSize: PAGE_LIMIT_TEN,
    });

  const [selectedModule, setSelectedModule] = useState<string>("all");
  const [selectedModel, setSelectedModel] = useState<string>("all");

  const [selectedToAssign, setSelectedToAssign] = useState<string[]>([]);
  const [selectedToUnassign, setSelectedToUnassign] = useState<string[]>([]);
  const [assignSelectionNames, setAssignSelectionNames] = useState<
    Record<string, string>
  >({});
  const [unassignSelectionNames, setUnassignSelectionNames] = useState<
    Record<string, string>
  >({});
  const [pendingPermissionIds, setPendingPermissionIds] = useState<Set<string>>(
    new Set(),
  );
  const [reviewModalType, setReviewModalType] = useState<
    "assign" | "unassign" | null
  >(null);

  const getPermissionLabel = useCallback(
    (permission: TPermission) =>
      permission.name ?? t("PERMISSION_ASSIGNMENT.UNNAMED_PERMISSION"),
    [t],
  );

  const removeAssignSelectionName = useCallback((id: string) => {
    setAssignSelectionNames((prev) => {
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const removeUnassignSelectionName = useCallback((id: string) => {
    setUnassignSelectionNames((prev) => {
      const { [id]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleDeselectAssign = useCallback(
    (id: string) => {
      setSelectedToAssign((prev) => prev.filter((pid) => pid !== id));
      removeAssignSelectionName(id);
    },
    [removeAssignSelectionName],
  );

  const handleDeselectUnassign = useCallback(
    (id: string) => {
      setSelectedToUnassign((prev) => prev.filter((pid) => pid !== id));
      removeUnassignSelectionName(id);
    },
    [removeUnassignSelectionName],
  );

  const clearSelectedToAssign = useCallback(() => {
    setSelectedToAssign([]);
    setAssignSelectionNames({});
  }, []);

  const clearSelectedToUnassign = useCallback(() => {
    setSelectedToUnassign([]);
    setUnassignSelectionNames({});
  }, []);

  const [currentAction, setCurrentAction] = useState<
    "assign" | "remove" | null
  >(null);
  const [isNavigatingBack, setIsNavigatingBack] = useState(false);
  const hasAssignedPaginationChanged = useRef(false);
  const hasAvailablePaginationChanged = useRef(false);

  useEffect(() => {
    return () => {
      void queryClient.invalidateQueries({
        queryKey: [GET_ROLE_LIST_QUERY_KEY, "enhanced-role-list"],
        refetchType: "inactive",
      });
    };
  }, [queryClient]);

  const { data: roleResponse } = useGetRoleByIdQuery(roleId!, {
    enabled: !!roleId && hasAnyPermission(["can-view-permissions"]),
  });

  const selectedRole = roleResponse;

  const breadcrumbRoutes = [
    {
      name: t("common:SIDEBAR_LABEL.CONFIGURATION"),
      path: "/configurations",
      icon: <Users />,
    },
    {
      name: t("TITLE"),
      path: "/role-management",
      icon: <Users />,
    },
    ...(roleId && selectedRole
      ? [
          {
            name: t("PERMISSION_ASSIGNMENT.TITLE"),
            path: `/role-management/assign-permission/${roleId}`,
            icon: <Shield />,
          },
        ]
      : []),
  ];
  useBreadcrumb(breadcrumbRoutes);

  const debouncedSearchTerm = useDebounce(searchTerm, 2000);

  // Fetch permissions KPI data
  const { data: kpiData, isLoading: isKpiLoading } = useGetPermissionKPIQuery(
    roleId!,
    {
      enabled: !!roleId && hasAnyPermission(["can-view-permissions"]),
    },
  );

  // Fetch permission modules & model list for the filters
  const { data: permissionModules, isLoading: isPermissionModulesLoading } =
    usePermissionModules({
      enabled: hasAnyPermission(["can-view-permissions"]),
    });

  // Memoized modules and available models
  const modules = useMemo(() => {
    return permissionModules?.modules ?? [];
  }, [permissionModules]);

  const availableModels = useMemo(() => {
    if (selectedModule === "all") return [];

    const module = modules.find((m) => m.app_label === selectedModule);
    return module?.models ?? [];
  }, [modules, selectedModule]);

  // Fetch assigned permissions
  const {
    data: assignedPermissionsData,
    isLoading: isAssignedInitialLoading,
    error: assignedPermissionsError,
    refetch: refetchAssignedPermissions,
    isFetching: isFetchingAssignedPermissions,
  } = useAssignedPermissions(
    roleId!,
    {
      search: debouncedSearchTerm || undefined,
      page: assignedPagination.pageIndex + 1,
      page_size: assignedPagination.pageSize,
      app_label: selectedModule !== "all" ? selectedModule : undefined,
      model: selectedModel !== "all" ? selectedModel : undefined,
    },
    {
      enabled: !!roleId && hasAnyPermission(["can-view-permissions"]),
      keepPreviousData: true,
    },
  );

  // Fetch available permissions
  const {
    data: availablePermissionsData,
    isLoading: isAvailableInitialLoading,
    error: availablePermissionsError,
    refetch: refetchAvailablePermissions,
    isFetching: isFetchingAvailablePermissions,
  } = useAvailablePermissions(
    roleId!,
    {
      search: debouncedSearchTerm || undefined,
      page: availablePagination.pageIndex + 1,
      page_size: availablePagination.pageSize,
      app_label: selectedModule !== "all" ? selectedModule : undefined,
      model: selectedModel !== "all" ? selectedModel : undefined,
    },
    {
      enabled: !!roleId && hasAnyPermission(["can-view-permissions"]),
    },
  );

  const refetchPermissionBoxes = useCallback(async () => {
    await Promise.all([
      refetchAvailablePermissions(),
      refetchAssignedPermissions(),
    ]);
  }, [refetchAvailablePermissions, refetchAssignedPermissions]);

  const handleBackToRoles = useCallback(async () => {
    if (isNavigatingBack) return;
    setIsNavigatingBack(true);
    try {
      await queryClient.invalidateQueries({
        queryKey: [GET_ROLE_LIST_QUERY_KEY, "enhanced-role-list"],
        refetchType: "inactive",
      });
      await navigate({ to: "/role-management" });
    } catch (error) {
      setIsNavigatingBack(false);
      throw error;
    }
  }, [isNavigatingBack, navigate, queryClient]);

  // API hooks for permission management
  const permissionToggleMutation = usePermissionToggle();

  const bulkAssignmentMutation = useBulkPermissionAssignment();

  // Initial loading state
  const isInitialLoading =
    isAssignedInitialLoading || isAvailableInitialLoading;

  // KPI counts
  const assignedPermissionsKPICount = assignedPermissionsData?.count ?? 0;
  const availablePermissionsKPICount = availablePermissionsData?.count ?? 0;

  // Page counts
  const assignedPageCount = Math.ceil(
    assignedPermissionsKPICount / assignedPagination.pageSize,
  );

  const availablePageCount = Math.ceil(
    availablePermissionsKPICount / availablePagination.pageSize,
  );

  // Memoized assigned and available permission lists
  const assignedPermissions = useMemo(
    () => assignedPermissionsData?.results ?? [],
    [assignedPermissionsData],
  );

  const availablePermissions = useMemo(
    () => availablePermissionsData?.results ?? [],
    [availablePermissionsData],
  );

  // Handler for toggling a single permission
  const handlePermissionToggle = useCallback(
    async (permission: TPermission) => {
      if (
        !permission.is_assigned &&
        !hasAnyPermission(["can-assign-permissions"])
      ) {
        return;
      }
      const permissionInternalId = permission.internal_id;
      if (!permissionInternalId) return;
      setPendingPermissionIds((prev) =>
        new Set(prev).add(permissionInternalId),
      );

      try {
        await permissionToggleMutation.mutateAsync({
          roleId: roleId!,
          permissionId: permissionInternalId,
          isAssigned: permission.is_assigned,
        });
        await refetchPermissionBoxes();
      } finally {
        setPendingPermissionIds((prev) => {
          const updated = new Set(prev);
          updated.delete(permissionInternalId);
          return updated;
        });
        setSelectedToAssign((prev) =>
          prev.filter((id) => id !== permissionInternalId),
        );
        setSelectedToUnassign((prev) =>
          prev.filter((id) => id !== permissionInternalId),
        );
        removeAssignSelectionName(permissionInternalId);
        removeUnassignSelectionName(permissionInternalId);
      }
    },
    [
      hasAnyPermission,
      permissionToggleMutation,
      refetchPermissionBoxes,
      roleId,
      removeAssignSelectionName,
      removeUnassignSelectionName,
    ],
  );

  // Handler for bulk assign and unassign permissions
  const handleBulkAssign = useCallback(async () => {
    if (!hasAnyPermission(["can-assign-permissions"])) return;
    if (selectedToAssign?.length === 0) return;
    setCurrentAction("assign");
    try {
      await bulkAssignmentMutation.mutateAsync({
        role_id: roleId!,
        permission_ids: selectedToAssign,
        action: "assign",
      });
      await refetchPermissionBoxes();
      clearSelectedToAssign();
    } finally {
      setCurrentAction(null);
    }
  }, [
    selectedToAssign,
    bulkAssignmentMutation,
    refetchPermissionBoxes,
    roleId,
    hasAnyPermission,
    clearSelectedToAssign,
  ]);

  const handleBulkUnassign = useCallback(async () => {
    if (!hasAnyPermission(["can-assign-permissions"])) return;
    if (selectedToUnassign?.length === 0) return;
    setCurrentAction("remove");
    try {
      await bulkAssignmentMutation.mutateAsync({
        role_id: roleId!,
        permission_ids: selectedToUnassign,
        action: "remove",
      });
      await refetchPermissionBoxes();
      clearSelectedToUnassign();
    } finally {
      setCurrentAction(null);
    }
  }, [
    selectedToUnassign,
    bulkAssignmentMutation,
    refetchPermissionBoxes,
    roleId,
    hasAnyPermission,
    clearSelectedToUnassign,
  ]);

  // Handlers for Select All
  const handleSelectAllAvailable = useCallback(() => {
    if (availablePermissions.length === 0) return;

    const currentIds = availablePermissions
      .map((p) => p.internal_id)
      .filter((id): id is string => Boolean(id));
    const allSelected = currentIds.every((id) => selectedToAssign.includes(id));

    if (allSelected) {
      setSelectedToAssign((prev) =>
        prev.filter((id) => !currentIds.includes(id)),
      );
      setAssignSelectionNames((prev) => {
        const next = { ...prev };
        currentIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    } else {
      setSelectedToAssign((prev) => [
        ...prev,
        ...currentIds.filter((id) => !prev.includes(id)),
      ]);
      setAssignSelectionNames((prev) => ({
        ...prev,
        ...Object.fromEntries(
          availablePermissions
            .filter((p) => p.internal_id && currentIds.includes(p.internal_id))
            .map((p) => [p.internal_id, getPermissionLabel(p)]),
        ),
      }));
    }
  }, [availablePermissions, selectedToAssign, getPermissionLabel]);

  const handleSelectAllAssigned = useCallback(() => {
    if (assignedPermissions.length === 0) return;

    const currentIds = assignedPermissions
      .map((p) => p.internal_id)
      .filter((id): id is string => Boolean(id));
    const allSelected = currentIds.every((id) =>
      selectedToUnassign.includes(id),
    );

    if (allSelected) {
      setSelectedToUnassign((prev) =>
        prev.filter((id) => !currentIds.includes(id)),
      );
      setUnassignSelectionNames((prev) => {
        const next = { ...prev };
        currentIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    } else {
      setSelectedToUnassign((prev) => [
        ...prev,
        ...currentIds.filter((id) => !prev.includes(id)),
      ]);
      setUnassignSelectionNames((prev) => ({
        ...prev,
        ...Object.fromEntries(
          assignedPermissions
            .filter((p) => p.internal_id && currentIds.includes(p.internal_id))
            .map((p) => [p.internal_id, getPermissionLabel(p)]),
        ),
      }));
    }
  }, [assignedPermissions, selectedToUnassign, getPermissionLabel]);

  const handleSelectPermission = useCallback(
    (permission: TPermission) => {
      const { internal_id: internalId, is_assigned } = permission;
      if (!internalId) return;

      if (is_assigned) {
        setSelectedToUnassign((prev) => {
          const isSelected = prev.includes(internalId);
          if (isSelected) {
            removeUnassignSelectionName(internalId);
            return prev.filter((pid) => pid !== internalId);
          }
          setUnassignSelectionNames((names) => ({
            ...names,
            [internalId]: getPermissionLabel(permission),
          }));
          return [...prev, internalId];
        });
      } else {
        setSelectedToAssign((prev) => {
          const isSelected = prev.includes(internalId);
          if (isSelected) {
            removeAssignSelectionName(internalId);
            return prev.filter((pid) => pid !== internalId);
          }
          setAssignSelectionNames((names) => ({
            ...names,
            [internalId]: getPermissionLabel(permission),
          }));
          return [...prev, internalId];
        });
      }
    },
    [
      getPermissionLabel,
      removeAssignSelectionName,
      removeUnassignSelectionName,
    ],
  );

  const clearSelectedPermissions = useCallback(() => {
    clearSelectedToAssign();
    clearSelectedToUnassign();
  }, [clearSelectedToAssign, clearSelectedToUnassign]);

  const handleAvailablePageSizeChange = useCallback(
    (size: number) => {
      clearSelectedPermissions();
      setAvailablePagination({
        pageIndex: 0,
        pageSize: size,
      });
    },
    [clearSelectedPermissions],
  );

  const handleAssignedPageSizeChange = useCallback(
    (size: number) => {
      clearSelectedPermissions();
      setAssignedPagination({
        pageIndex: 0,
        pageSize: size,
      });
    },
    [clearSelectedPermissions],
  );

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setSelectedModule("all");
    setSelectedModel("all");
    setAssignedPagination({ pageIndex: 0, pageSize: PAGE_LIMIT_TEN });
    setAvailablePagination({ pageIndex: 0, pageSize: PAGE_LIMIT_TEN });
  }, []);

  // Reset pagination when RAW search changes (NOT debounced) and also  when filters change
  useEffect(() => {
    setAssignedPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setAvailablePagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [searchTerm, selectedModule, selectedModel]);

  // Ensure fresh API calls when search is cleared.
  useEffect(() => {
    if (debouncedSearchTerm?.trim() !== "") return;
    void refetchAssignedPermissions();
    void refetchAvailablePermissions();
  }, [
    debouncedSearchTerm,
    refetchAssignedPermissions,
    refetchAvailablePermissions,
  ]);

  // Force fresh API calls when pagination changes (even if query cache is still fresh)
  useEffect(() => {
    if (!hasAssignedPaginationChanged.current) {
      hasAssignedPaginationChanged.current = true;
      return;
    }
    void refetchAssignedPermissions();
  }, [
    assignedPagination.pageIndex,
    assignedPagination.pageSize,
    refetchAssignedPermissions,
  ]);

  useEffect(() => {
    if (!hasAvailablePaginationChanged.current) {
      hasAvailablePaginationChanged.current = true;
      return;
    }
    void refetchAvailablePermissions();
  }, [
    availablePagination.pageIndex,
    availablePagination.pageSize,
    refetchAvailablePermissions,
  ]);

  const hasActiveFilters =
    searchTerm !== "" || selectedModule !== "all" || selectedModel !== "all";

  const selectedModuleLabel = useMemo(() => {
    if (selectedModule === "all") {
      return t("PERMISSION_ASSIGNMENT.ALL_MODULES");
    }

    const module = modules.find((m) => m.app_label === selectedModule);
    return module?.display_name ?? selectedModule;
  }, [selectedModule, modules, t]);

  const selectedModelLabel = useMemo(() => {
    if (selectedModel === "all") {
      return t("PERMISSION_ASSIGNMENT.ALL_MODELS");
    }

    return (
      availableModels.find((m) => m.model === selectedModel)?.display_name ??
      selectedModel
    );
  }, [selectedModel, availableModels, t]);

  const isAnySingleActionPending = pendingPermissionIds.size > 0;

  const isBulkAssigning =
    bulkAssignmentMutation.isPending && currentAction === "assign";

  const isBulkRemoving =
    bulkAssignmentMutation.isPending && currentAction === "remove";

  const reviewModalConfig = useMemo(() => {
    if (reviewModalType === "assign") {
      return {
        title: t("PERMISSION_ASSIGNMENT.REVIEW_SELECTED_TO_ASSIGN"),
        selectedIds: selectedToAssign,
        selectionNames: assignSelectionNames,
        onDeselect: handleDeselectAssign,
      };
    }

    if (reviewModalType === "unassign") {
      return {
        title: t("PERMISSION_ASSIGNMENT.REVIEW_SELECTED_TO_REMOVE"),
        selectedIds: selectedToUnassign,
        selectionNames: unassignSelectionNames,
        onDeselect: handleDeselectUnassign,
      };
    }

    return null;
  }, [
    reviewModalType,
    selectedToAssign,
    assignSelectionNames,
    selectedToUnassign,
    unassignSelectionNames,
    handleDeselectAssign,
    handleDeselectUnassign,
    t,
  ]);

  useEffect(() => {
    if (
      reviewModalType === "assign" &&
      (isFetchingAvailablePermissions || selectedToAssign.length === 0)
    ) {
      setReviewModalType(null);
    }
  }, [
    reviewModalType,
    isFetchingAvailablePermissions,
    selectedToAssign.length,
  ]);

  useEffect(() => {
    if (
      reviewModalType === "unassign" &&
      (isFetchingAssignedPermissions || selectedToUnassign.length === 0)
    ) {
      setReviewModalType(null);
    }
  }, [
    reviewModalType,
    isFetchingAssignedPermissions,
    selectedToUnassign.length,
  ]);

  // KPI
  const renderRoleKpiCard = (
    type: "total" | "assigned" | "available",
    count: number,
    title: string,
  ) => {
    const kpiConfig = {
      total: {
        icon: <Shield className="w-6 h-6 text-blue" />,
        bg: "bg-blue/10",
      },
      assigned: {
        icon: <CheckCircle className="w-6 h-6 text-green" />,
        bg: "bg-green/10",
      },
      available: {
        icon: <Circle className="w-6 h-6 text-muted-foreground" />,
        bg: "bg-gray/20 dark:bg-gray-700",
      },
    };

    const { icon, bg } = kpiConfig[type];

    return (
      <CommonCardUI
        className="custom-bx-shadow bg-background transition hover:-translate-y-1 hover:shadow-xl"
        classNameContent="p-4"
      >
        <div className="flex items-center gap-4">
          {/* Icon */}
          <div
            className={cn(
              "w-12 h-12 rounded-lg flex items-center justify-center",
              bg,
            )}
          >
            {icon}
          </div>

          {/* Text */}
          <div className="flex flex-col">
            <Typography
              variant="h3"
              affects="semiBold"
              className="text-foreground"
            >
              {count}
            </Typography>
            <Typography
              variant="p"
              affects="semiBold"
              className="text-muted-foreground"
            >
              {title}
            </Typography>
          </div>
        </div>
      </CommonCardUI>
    );
  };

  // Error state
  if (assignedPermissionsError || availablePermissionsError) {
    return (
      <CommonCardUI
        className="custom-bx-shadow bg-background"
        classNameContent="p-4"
      >
        <Button
          type="button"
          variant="outline"
          size={"sm"}
          onClick={() => void handleBackToRoles()}
          className="flex items-center gap-2"
          disabled={isNavigatingBack}
        >
          {isNavigatingBack ? (
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <ArrowLeft className="w-4 h-4 test" />
          )}
          {t("PERMISSION_ASSIGNMENT.BACK_TO_ROLES")}
        </Button>
        <ErrorFallback
          isError={true}
          errormsg={t("PERMISSION_ASSIGNMENT.FAILED_TO_LOAD_PERMISSIONS")}
        />
      </CommonCardUI>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {isKpiLoading ? (
        <div className="border dark:border-gray-700 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <KpiLoaders numberOfLoaders={1} className="w-8 h-8" />
              <div className="space-y-2">
                <KpiLoaders numberOfLoaders={1} className="w-32 h-6" />
                <KpiLoaders numberOfLoaders={1} className="w-48 h-4" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <KpiLoaders numberOfLoaders={1} className="h-4 w-24" />
              <KpiLoaders numberOfLoaders={1} className="h-6 w-16" />
            </div>
            <div className="space-y-2">
              <KpiLoaders numberOfLoaders={1} className="h-4 w-20" />
              <KpiLoaders numberOfLoaders={1} className="h-6 w-12" />
            </div>
            <div className="space-y-2">
              <KpiLoaders numberOfLoaders={1} className="h-4 w-16" />
              <KpiLoaders numberOfLoaders={1} className="h-6 w-14" />
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Header */}
          <CommonCardUI classNameContent="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Typography variant="h3" className="font-semibold">
                  {t("PERMISSION_ASSIGNMENT.TITLE")}
                </Typography>
                <Badge
                  variant={selectedRole?.is_active ? "default" : "secondary"}
                >
                  {selectedRole?.is_active
                    ? t("GLOBAL.ACTIVE")
                    : t("GLOBAL.INACTIVE")}
                </Badge>
              </div>
              <div className="flex flex-col">
                <Typography
                  variant="small"
                  className="text-muted-foreground gap-2"
                >
                  {t("PERMISSION_ASSIGNMENT.MANAGE_PERMISSIONS")}
                </Typography>
                <Typography variant="small" className="font-semibold">
                  Role Name : {selectedRole?.name ?? ""}
                </Typography>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={() => void handleBackToRoles()}
              className="flex items-center gap-2"
              disabled={isNavigatingBack}
            >
              {isNavigatingBack ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <MoveLeft size={16} />
              )}
              {t("PERMISSION_ASSIGNMENT.BACK_TO_ROLES")}
            </Button>
          </CommonCardUI>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {renderRoleKpiCard(
              "total",
              kpiData?.data?.total_permissions ?? 0,
              t("PERMISSION_ASSIGNMENT.TOTAL_PERMISSIONS"),
            )}

            {renderRoleKpiCard(
              "assigned",
              kpiData?.data?.assigned_count ?? 0,
              t("PERMISSION_ASSIGNMENT.ASSIGNED"),
            )}

            {renderRoleKpiCard(
              "available",
              kpiData?.data?.available_count ?? 0,
              t("PERMISSION_ASSIGNMENT.AVAILABLE"),
            )}
          </div>
        </>
      )}
      <CommonCardUI
        className="custom-bx-shadow bg-background"
        classNameContent="p-0"
      >
        <div className="border-b border-dashed p-3">
          {/* Search and Filters */}
          {isPermissionModulesLoading ? (
            <div className="border dark:border-gray-700 rounded-lg shadow p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <KpiLoaders numberOfLoaders={1} className="flex-1 h-10" />
                <KpiLoaders numberOfLoaders={1} className="w-40 h-10" />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Search, Module, Model filters and Clear button in one row */}
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Search */}
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />

                    <Input
                      type="text"
                      placeholder={t(
                        "PERMISSION_ASSIGNMENT.SEARCH_PLACEHOLDER",
                      )}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`pl-10 py-2 w-full ${isInitialLoading ? "pr-10" : "pr-4"}`}
                    />
                  </div>
                </div>

                {/* Modules Dropdown*/}
                <div className="flex-1">
                  <div className="dropdown-main-section items-center">
                    <Dropdown
                      key={selectedModule}
                      label={selectedModuleLabel}
                      dismissOnClick={true}
                      size="default"
                      className="w-full flex items-center justify-between rounded border border-gray-300 bg-background px-2 py-2 text-sm text-foreground hover:border-gray-400 dark:border-gray-700"
                    >
                      <DropdownItem
                        key="all"
                        className="hover:bg-slate-200"
                        onClick={() => {
                          setSelectedModule("all");
                          setSelectedModel("all");
                        }}
                      >
                        {t("PERMISSION_ASSIGNMENT.ALL_MODULES")}
                      </DropdownItem>

                      {modules?.map((module) => (
                        <DropdownItem
                          key={module.app_label}
                          className="hover:bg-slate-200"
                          onClick={() => {
                            setSelectedModule(module.app_label);
                            setSelectedModel("all");
                          }}
                        >
                          {module.display_name}
                        </DropdownItem>
                      ))}
                    </Dropdown>
                  </div>
                </div>

                {/* Models Dropdown*/}
                <div className="flex-1">
                  <div className="dropdown-main-section items-center">
                    <Dropdown
                      key={selectedModel}
                      label={selectedModelLabel}
                      dismissOnClick={true}
                      size="default"
                      disabled={selectedModule === "all"}
                      className={`w-full flex items-center justify-between rounded border bg-background px-2 py-2 text-sm text-foreground 
                      ${selectedModule === "all" ? "cursor-not-allowed" : ""}`}
                    >
                      <DropdownItem
                        key="all"
                        className="hover:bg-slate-200"
                        onClick={() => setSelectedModel("all")}
                      >
                        {t("PERMISSION_ASSIGNMENT.ALL_MODELS")}
                      </DropdownItem>

                      {availableModels?.map((model) => (
                        <DropdownItem
                          key={model?.model}
                          className="hover:bg-slate-200"
                          onClick={() => setSelectedModel(model?.model)}
                        >
                          {model?.display_name}
                        </DropdownItem>
                      ))}
                    </Dropdown>
                  </div>
                </div>

                {/* Clear Filters Button */}
                <div className="flex-shrink-0">
                  {hasActiveFilters && (
                    <Button variant="outline" size="sm" onClick={clearFilters}>
                      {t("GLOBAL.CLEAR_FILTERS")}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-3">
          {/* Available Permissions */}
          <div className="border border-gray-300 dark:border-gray-700 rounded-lg h-[400px] flex flex-col">
            <div className="px-3 py-2 border-b border-border flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="rounded border-border mr-2 cursor-pointer"
                    checked={
                      availablePermissions.length > 0 &&
                      availablePermissions.every((p) =>
                        selectedToAssign.includes(p.internal_id),
                      )
                    }
                    onChange={handleSelectAllAvailable}
                    disabled={availablePermissions.length === 0}
                    id="select-all-available-checkbox"
                  />
                  <Typography variant="p" className="font-semibold text-base">
                    {t("PERMISSION_ASSIGNMENT.AVAILABLE_PERMISSIONS")}
                  </Typography>
                  <Badge
                    variant="secondary"
                    className="text-xs px-2 py-0.5 min-h-0 leading-none"
                  >
                    {availablePermissionsKPICount}
                  </Badge>
                </div>
                {selectedToAssign?.length > 0 && (
                  <PermissionGuard permissions={["can-assign-permissions"]}>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => void handleBulkAssign()}
                      disabled={
                        selectedToAssign.length === 0 ||
                        isAnySingleActionPending ||
                        bulkAssignmentMutation.isPending
                      }
                    >
                      {isBulkAssigning ? (
                        <>
                          <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin mr-2" />
                          {t("PERMISSION_ASSIGNMENT.ASSIGNING")}
                        </>
                      ) : (
                        `${t("PERMISSION_ASSIGNMENT.ASSIGN_SELECTED")} (${selectedToAssign.length})`
                      )}
                    </Button>
                  </PermissionGuard>
                )}
              </div>
            </div>

            <PermissionSelectionPanel
              selectedIds={selectedToAssign}
              isListLoading={isFetchingAvailablePermissions}
              onReviewClick={() => setReviewModalType("assign")}
            />

            <div className="flex-1 overflow-y-auto min-h-0">
              {isFetchingAvailablePermissions ? (
                <KpiLoaders numberOfLoaders={5} />
              ) : availablePermissions?.length > 0 ? (
                availablePermissions?.map((permission: TAssignedPermission) => (
                  <PermissionRow
                    key={permission.id}
                    permission={permission}
                    isChecked={selectedToAssign?.includes(
                      permission.internal_id,
                    )}
                    isToggling={pendingPermissionIds.has(
                      permission.internal_id,
                    )}
                    isBulkActionRunning={bulkAssignmentMutation.isPending}
                    onSelect={handleSelectPermission}
                    onToggle={handlePermissionToggle}
                  />
                ))
              ) : (
                <div className="text-center py-8">
                  <Circle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <Typography
                    variant="p"
                    affects="muted"
                    className="dark:text-muted-foreground"
                  >
                    {t("PERMISSION_ASSIGNMENT.NO_AVAILABLE_PERMISSIONS")}
                  </Typography>
                </div>
              )}
            </div>

            {availablePermissions?.length > 0 && (
              <div className="p-4 border-t border-border flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 dropdown-main-section justify-end">
                    <Typography
                      variant="small"
                      className="text-muted-foreground"
                    >
                      {t("GLOBAL.SHOW")}:
                    </Typography>
                    <Dropdown
                      label={availablePagination.pageSize.toString()}
                      dismissOnClick={true}
                      size="default"
                      className="w-20 flex items-center justify-between rounded border border-gray-300 bg-background px-2 py-1 text-sm text-foreground hover:border-gray-400 dark:border-gray-700"
                    >
                      {[5, 10, 20, 50, 100].map((size) => (
                        <DropdownItem
                          key={size}
                          className="hover:bg-slate-200"
                          onClick={() => handleAvailablePageSizeChange(size)}
                        >
                          {size}
                        </DropdownItem>
                      ))}
                    </Dropdown>
                  </div>
                  <div className="mt-auto ">
                    <CommonPaginationCard
                      totalRecordCount={availablePermissionsData?.count ?? 0}
                      page={availablePagination.pageIndex}
                      pageCount={availablePageCount}
                      rowsPerPage={availablePagination.pageSize}
                      onPageChange={(page) =>
                        setAvailablePagination((prev) => ({
                          ...prev,
                          pageIndex: page,
                        }))
                      }
                      onRowsPerPageChange={handleAvailablePageSizeChange}
                      disablePrev={availablePagination.pageIndex === 0}
                      disableNext={
                        availablePagination.pageIndex >= availablePageCount - 1
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Assigned Permissions */}
          <div className="border border-gray-300 dark:border-gray-700 rounded-lg h-[400px] flex flex-col">
            <div className="px-3 py-2 border-b border-border flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="rounded border-border mr-2 cursor-pointer"
                    checked={
                      assignedPermissions.length > 0 &&
                      assignedPermissions.every((p) =>
                        selectedToUnassign.includes(p.internal_id),
                      )
                    }
                    onChange={handleSelectAllAssigned}
                    disabled={assignedPermissions.length === 0}
                    id="select-all-assigned-checkbox"
                  />
                  <Typography variant="p" className="font-semibold text-base">
                    {t("PERMISSION_ASSIGNMENT.ASSIGNED_PERMISSIONS")}
                  </Typography>
                  <Badge
                    variant="default"
                    className="text-xs px-2 py-0.5 min-h-0 leading-none"
                  >
                    {assignedPermissionsKPICount}
                  </Badge>
                </div>
                {selectedToUnassign?.length > 0 && (
                  <PermissionGuard permissions={["can-assign-permissions"]}>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => void handleBulkUnassign()}
                      disabled={
                        selectedToUnassign.length === 0 ||
                        isAnySingleActionPending ||
                        bulkAssignmentMutation.isPending
                      }
                    >
                      {isBulkRemoving ? (
                        <>
                          <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin mr-2" />
                          {t("PERMISSION_ASSIGNMENT.REMOVING")}
                        </>
                      ) : (
                        `${t("PERMISSION_ASSIGNMENT.REMOVE_SELECTED")} (${selectedToUnassign.length})`
                      )}
                    </Button>
                  </PermissionGuard>
                )}
              </div>
            </div>

            <PermissionSelectionPanel
              selectedIds={selectedToUnassign}
              isListLoading={isFetchingAssignedPermissions}
              onReviewClick={() => setReviewModalType("unassign")}
            />

            <div className="flex-1 overflow-y-auto min-h-0">
              {isFetchingAssignedPermissions ? (
                <KpiLoaders numberOfLoaders={5} />
              ) : assignedPermissions.length > 0 ? (
                assignedPermissions.map((permission) => (
                  <PermissionRow
                    key={permission.id}
                    permission={permission}
                    isChecked={selectedToUnassign?.includes(
                      permission.internal_id,
                    )}
                    isToggling={pendingPermissionIds.has(
                      permission.internal_id,
                    )}
                    isBulkActionRunning={bulkAssignmentMutation.isPending}
                    onSelect={handleSelectPermission}
                    onToggle={handlePermissionToggle}
                  />
                ))
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <Typography
                    variant="p"
                    affects="muted"
                    className="dark:text-muted-foreground"
                  >
                    {t("PERMISSION_ASSIGNMENT.NO_ASSIGNED_PERMISSIONS")}
                  </Typography>
                </div>
              )}
            </div>

            {assignedPermissions?.length > 0 && (
              <div className="p-4 border-t border-border flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 dropdown-main-section justify-end">
                    <Typography
                      variant="small"
                      className="text-muted-foreground"
                    >
                      {t("GLOBAL.SHOW")}:
                    </Typography>
                    <Dropdown
                      label={assignedPagination.pageSize.toString()}
                      dismissOnClick={true}
                      size="default"
                      className="w-20 flex items-center justify-between rounded border border-gray-300 bg-background px-2 py-1 text-sm text-foreground hover:border-gray-400 dark:border-gray-700"
                    >
                      {[5, 10, 20, 50, 100].map((size) => (
                        <DropdownItem
                          key={size}
                          className="hover:bg-slate-200"
                          onClick={() => handleAssignedPageSizeChange(size)}
                        >
                          {size}
                        </DropdownItem>
                      ))}
                    </Dropdown>
                  </div>

                  <div className="mt-auto ">
                    <CommonPaginationCard
                      totalRecordCount={assignedPermissionsData?.count ?? 0}
                      page={assignedPagination.pageIndex}
                      pageCount={assignedPageCount}
                      rowsPerPage={assignedPagination.pageSize}
                      onPageChange={(page) =>
                        setAssignedPagination((prev) => ({
                          ...prev,
                          pageIndex: page,
                        }))
                      }
                      onRowsPerPageChange={handleAssignedPageSizeChange}
                      disablePrev={assignedPagination.pageIndex === 0}
                      disableNext={
                        assignedPagination.pageIndex >= assignedPageCount - 1
                      }
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CommonCardUI>

      <PermissionSelectionReviewModal
        open={reviewModalType !== null}
        onClose={() => setReviewModalType(null)}
        title={reviewModalConfig?.title ?? ""}
        selectedIds={reviewModalConfig?.selectedIds ?? []}
        selectionNames={reviewModalConfig?.selectionNames ?? {}}
        onDeselect={reviewModalConfig?.onDeselect ?? (() => undefined)}
      />
    </div>
  );
};
